import { memo, useCallback, useState } from 'react'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import AIPromptText from 'components/AIPromptText'
import AISkeletonItem from 'components/AISkeletonItem'
import { AiGenerationLoadingContent } from 'components/AIGenerationLoadingContent'
import ActionsOverlay from 'common-components/ActionsOverlay'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { T } from 'consts/translationKeys'
import { DEFAULT_MEDIA_DURATION, PROJECT_MEDIA_TYPES } from 'consts'
import { AI_GENERATE_TAB_KEY } from 'containers/SubSidebar/AiImageContent/consts'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { useGenerationProgress } from 'hooks/useGenerationProgress'
import { dispatch } from 'store'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { SQUARE_ASPECT_RATIO, normalizeAspectRatio } from 'utils/aspectRatio'
import * as Styled from './styled'

interface IVersionHistoryItemProps {
  item: IVersionHistoryItem
}

const VersionHistoryItem = ({ item }: IVersionHistoryItemProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const addItemToTimeline = useAddAiItemToTimeline()

  const isProcessing = !!item.process

  const { progress, message } = useGenerationProgress({
    assetId: isProcessing ? item.id : undefined,
    isGenerating: isProcessing
  })

  const handleAddToTimeline = useCallback(() => {
    addItemToTimeline({
      type: PROJECT_MEDIA_TYPES.aiImage,
      path: item.url,
      fileName: `ai-image-${item.assetId}`,
      originalWidth: item.width ?? 0,
      originalHeight: item.height ?? 0,
      duration: DEFAULT_MEDIA_DURATION,
      assetId: String(item.assetId),
      assetParentId: String(item.id)
    })
  }, [addItemToTimeline, item])

  const handleRegenerate = useCallback(() => {
    if (!item.assetId) return
    dispatch(aiImageThunks.setGenerationParamsByAssetId(Number(item.assetId)))
    dispatch(aiImageThunks.setActiveTab(AI_GENERATE_TAB_KEY))
  }, [item])

  const handleDownload = useCallback(async () => {
    const fileName = `ai-image-${item.assetId ?? item.id}`
    await downloadMedia({ filePath: item.url, fileName })
  }, [item])

  return (
    <Styled.VersionItemContainer>
      {item.prompt && <AIPromptText promptText={item.prompt} />}
      {(item.modelName || item.modelQuality) && (
        <Styled.MetadataBadges>
          {item.modelName && (
            <Styled.MetadataBadge>{item.modelName}</Styled.MetadataBadge>
          )}
          {item.modelQuality && (
            <Styled.MetadataBadge>{item.modelQuality}</Styled.MetadataBadge>
          )}
        </Styled.MetadataBadges>
      )}
      {isProcessing ? (
        <AiGenerationLoadingContent
          overlay
          progress={progress}
          message={message}
          aspectRatio={normalizeAspectRatio(
            item.aspectRatio || SQUARE_ASPECT_RATIO
          )}
        />
      ) : (
        <Styled.MediaWrapper>
          {isLoading && <AISkeletonItem width="100%" height="100%" />}
          <Styled.ImageElement
            src={item.url}
            onLoad={() => setIsLoading(false)}
            $isLoading={isLoading}
          />
          {!isLoading && (
            <ActionsOverlay
              actions={{
                mainAction: handleAddToTimeline,
                primaryActions: [
                  {
                    key: T.RE_GENERATE,
                    title: translate(T.RE_GENERATE),
                    icon: <RegenerateIcon />,
                    onClick: handleRegenerate
                  },
                  {
                    key: T.DOWNLOAD,
                    title: translate(T.DOWNLOAD),
                    icon: <DownloadIcon />,
                    onClick: handleDownload
                  }
                ]
              }}
            />
          )}
        </Styled.MediaWrapper>
      )}
    </Styled.VersionItemContainer>
  )
}

export default memo(VersionHistoryItem)
